import { useEffect, useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { Link } from 'react-router-dom'
import { MultiSelect, NumberInput, Select } from '@mantine/core'
import { Timestamp } from 'firebase/firestore'
import { can, describeCaptureWindow } from '@racedash/shared'
import type { Entry } from '@racedash/shared'
import { useCars, useDrivers } from '../team/api'
import { useSeries } from '../events/series'
import { useShell } from '../AppShell'
import OrgNav from '../components/OrgNav'
import {
  attachToEvent,
  detachFromEvent,
  updateAttachment,
  useAttachments,
  useUpcomingEvents,
} from '../events/api'
import type { Attachment, CatalogueEvent } from '../events/api'
import { useAttachedEvents } from '../dashboard/useDashboardSource'

/**
 * Choosing which events to be at.
 *
 * Attaching is the only thing that causes an event to be captured, so this page
 * is where a team decides what will and will not exist afterwards. That is
 * worth being blunt about on the page itself: RIS publishes no history, so an
 * unattached session is not "available later", it is gone.
 */
export default function Events() {
  const { user, currentOrg, role } = useShell()
  const { events, error } = useUpcomingEvents()
  const { attachments } = useAttachments(currentOrg?.id)
  // Events this organisation has attached to, in BOTH directions in time.
  //
  // The upcoming list alone was not enough. A dashboard now reads whatever the
  // team has entered, including last month's race, so an attachment to a past
  // event is still live data — and this page was the only place to manage it
  // while being the one place it could not be seen.
  const { events: attached } = useAttachedEvents(currentOrg?.id)
  const [editing, setEditing] = useState<CatalogueEvent | null>(null)
  // Null until the attachments arrive, so the default can depend on whether
  // there are any. Chosen once rather than recomputed, so the view does not
  // jump under the reader when a snapshot lands.
  const [scope, setScope] = useState<'all' | 'mine' | null>(null)

  useEffect(() => {
    if (scope !== null || !attachments) return
    // After setup, the everyday question is "are our events in hand", so an
    // organisation that has attached to something starts on its own list. One
    // that has not would only see an empty table, which reads as broken.
    setScope(attachments.length > 0 ? 'mine' : 'all')
  }, [attachments, scope])

  if (!currentOrg) return <Text c="dimmed">Organisation not found.</Text>

  const mayAttach = can(role, 'dashboard.create')
  const byEvent = new Map((attachments ?? []).map((a) => [a.eventId, a]))

  // The catalogue's upcoming events, plus anything attached that has already
  // happened. Merged by id so an event that is both appears once.
  const listed = new Map((events ?? []).map((e) => [e.id, e]))
  for (const a of attached ?? []) {
    if (listed.has(a.eventId)) continue
    listed.set(a.eventId, {
      id: a.eventId,
      name: a.name,
      trackId: a.trackId ?? '',
      timezone: '',
      startsAt: Timestamp.fromDate(a.startsAt ?? new Date(0)),
      endsAt: Timestamp.fromDate(a.endsAt ?? new Date(0)),
      source: { vendor: 'ris', feed: 'json' },
    } as CatalogueEvent)
  }
  const all = [...listed.values()].sort(
    (a, b) => (a.startsAt?.toMillis() ?? 0) - (b.startsAt?.toMillis() ?? 0),
  )

  const showing = scope ?? 'all'
  const visible = all.filter((e) => showing === 'all' || byEvent.has(e.id))
  // Counted from what selecting it would actually show, not from the
  // attachments. Those disagreed the moment an attached event fell out of the
  // upcoming list, and a tab reading "Ours (2)" that then shows one is the kind
  // of small lie that makes somebody stop trusting the numbers on a page.
  const ourCount = all.filter((e) => byEvent.has(e.id)).length

  return (
    <Stack gap="lg">
      <div>
        <Title order={1} size="h2">
          Events
        </Title>
        <Text c="dimmed" size="sm">
          Say which events you are attending. Timing is only captured for events somebody has
          attached to — and it cannot be captured afterwards, so an event nobody attaches to leaves
          no record.
        </Text>
      </div>

      <OrgNav orgId={currentOrg.id} role={role} />

      {error && <Alert color="red">{error}</Alert>}
      {!events && !error && <Text c="dimmed">Loading…</Text>}

      {events && events.length > 0 && (
        <Group justify="space-between" align="center">
          <SegmentedControl
            value={showing}
            onChange={(v) => setScope(v as 'all' | 'mine')}
            data={[
              { label: `All events (${all.length})`, value: 'all' },
              // The count is on the label so the option is worth pressing
              // before it is pressed — an empty "Ours" is then obviously empty
              // rather than apparently broken.
              { label: `Ours (${ourCount})`, value: 'mine' },
            ]}
          />
          <Text size="xs" c="dimmed">
            {visible.length} shown
          </Text>
        </Group>
      )}

      {events?.length === 0 && (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            No upcoming events in the catalogue.
          </Text>
        </Paper>
      )}

      {events && events.length > 0 && visible.length === 0 && (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            You are not attending any upcoming events, so nothing will be captured. Switch to all
            events to pick one.
          </Text>
        </Paper>
      )}

      {visible.length > 0 && (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={780}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Event</Table.Th>
                  <Table.Th w={150}>Dates</Table.Th>
                  <Table.Th w={130}>Circuit</Table.Th>
                  <Table.Th w={230}>Capture</Table.Th>
                  <Table.Th w={150} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map((e) => {
                  const mine = byEvent.get(e.id)
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {e.name}
                        </Text>
                        <Badge
                          variant="light"
                          size="xs"
                          mt={2}
                          color={e.source?.vendor === 'ris' ? 'racedash' : 'teal'}
                        >
                          {e.source?.vendor}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatRange(e)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {e.trackId}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <CaptureState event={e} mine={mine} />
                      </Table.Td>
                      <Table.Td align="right">
                        {mayAttach && (
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Button
                              variant={mine ? 'subtle' : 'light'}
                              size="compact-sm"
                              onClick={() => setEditing(e)}
                            >
                              {mine ? 'Change' : 'Attend'}
                            </Button>
                            {/* Only for events you are at: a plan for a race
                                you are not entered in is nothing. */}
                            {mine && (
                              <Button
                                component={Link}
                                to={`/o/${currentOrg.id}/events/${e.id}/plan`}
                                variant="subtle"
                                size="compact-sm"
                              >
                                Plan
                              </Button>
                            )}
                            {mine && (
                              <Button
                                variant="subtle"
                                color="red"
                                size="compact-sm"
                                onClick={() => void detachFromEvent(e.id, currentOrg.id)}
                              >
                                Leave
                              </Button>
                            )}
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}

      <AttendDialog
        opened={editing !== null}
        onClose={() => setEditing(null)}
        event={editing}
        existing={editing ? byEvent.get(editing.id) : undefined}
        orgId={currentOrg.id}
        orgName={currentOrg.kind === 'personal' ? 'Personal' : currentOrg.name}
        uid={user}
      />
    </Stack>
  )
}

/**
 * What will actually happen for this event.
 *
 * Three states worth telling apart: not attending, attending but not capturable
 * yet, and attending with capture arranged. The middle one is the one that
 * matters — an event you have attached to that still has no address is the case
 * where somebody has to do something before the weekend.
 */
/**
 * Why this event will not be captured, or null if it will.
 *
 * Says the real reason rather than a generic one. "TSL events are not captured
 * yet" is something a person can act on — by not relying on it — where "no
 * timing address" invited them to go and find an address that would not have
 * helped.
 */
function captureBlocker(event: CatalogueEvent): string | null {
  // Mirrors notCapturable in cmd/reconcile. Both vendors have a capture
  // pipeline now, so what is left is the genuinely missing pieces rather than
  // a whole vendor — this said "TSL events are not captured yet" for a day
  // after they were.
  if (event.source.vendor === 'tsl') {
    return event.source.eventId ? null : 'no TSL event id'
  }
  if (event.source.feed !== 'json' && !event.source.path) {
    return 'RIS XML event with no path set'
  }
  return null
}

function CaptureState({ event, mine }: { event: CatalogueEvent; mine?: Attachment }) {
  if (!mine) {
    return (
      <Text size="xs" c="dimmed">
        Not attending — nothing will be recorded
      </Text>
    )
  }
  // What "cannot be captured" actually means now, per vendor.
  //
  // It used to mean "no timing address", and that concept is gone: a RIS event
  // needed a uuid found by hand from its own website, and the uuid turned out
  // to be an index into seventeen fixed channels rather than an address, so
  // capture sweeps the candidates instead. Every RIS JSON event is capturable.
  // Leaving the old wording up meant a perfectly capturable event sitting under
  // a yellow badge asking for something that no longer exists.
  const blocked = captureBlocker(event)
  if (blocked) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Badge variant="light" color="yellow" size="sm" style={{ flex: 'none' }}>
          not ready
        </Badge>
        <Text size="xs" c="dimmed">
          {blocked}
        </Text>
      </Group>
    )
  }
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Badge variant="light" color="green" size="sm" style={{ flex: 'none' }}>
        attending
      </Badge>
      <Text size="xs" c="dimmed">
        {describeCaptureWindow(mine)}
      </Text>
    </Group>
  )
}

function AttendDialog({
  opened,
  onClose,
  event,
  existing,
  orgId,
  orgName,
  uid,
}: {
  opened: boolean
  onClose: () => void
  event: CatalogueEvent | null
  existing?: Attachment
  orgId: string
  orgName: string
  uid: ReturnType<typeof useShell>['user']
}) {
  const [mode, setMode] = useState('default')
  const [from, setFrom] = useState('08:00')
  const [until, setUntil] = useState('18:00')
  const [entries, setEntries] = useState<Entry[]>([])
  const series = useSeries()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const { items: cars } = useCars(orgId)
  const { items: drivers } = useDrivers(orgId)

  useEffect(() => {
    if (!opened) return
    setFailed(null)
    if (existing?.continuous) setMode('continuous')
    else if (existing?.captureFrom || existing?.captureUntil) setMode('custom')
    else setMode('default')
    setFrom(existing?.captureFrom ?? '08:00')
    setUntil(existing?.captureUntil ?? '18:00')
    setEntries(existing?.entries ?? [])
  }, [opened, existing])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !event) return
    setBusy(true)
    setFailed(null)
    try {
      const input = {
        continuous: mode === 'continuous',
        captureFrom: mode === 'custom' ? from : null,
        captureUntil: mode === 'custom' ? until : null,
        entries,
      }
      if (existing) await updateAttachment(event.id, orgId, input)
      else await attachToEvent(event.id, orgId, orgName, uid, input)
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => !busy && onClose()}
      title={existing ? `Change ${event?.name}` : `Attend ${event?.name}`}
      size="lg"
    >
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}

          <Text size="sm" c="dimmed">
            Attaching is what causes this event to be captured. One capture serves everybody
            attending, so it costs nothing extra if another team is here too.
          </Text>

          <div>
            <Text size="sm" fw={500} mb={4}>
              When is anything on track?
            </Text>
            <SegmentedControl
              fullWidth
              value={mode}
              onChange={setMode}
              data={[
                { label: 'Daytime (08:00–18:00)', value: 'default' },
                { label: 'Through the night', value: 'continuous' },
                { label: 'Custom hours', value: 'custom' },
              ]}
            />
            <Text size="xs" c="dimmed" mt={6}>
              {mode === 'continuous'
                ? 'For a race that runs overnight — a 24 or 25 hour, say. Capture runs continuously from the first morning to the last evening.'
                : mode === 'custom'
                  ? "Use this if the meeting starts early or runs late. It is the circuit's local time."
                  : 'Covers a normal meeting. Nothing is on track outside these hours, so nothing is missed.'}
            </Text>
          </div>

          {mode === 'custom' && (
            <Group grow>
              <TextInput
                label="From"
                type="time"
                value={from}
                onChange={(e) => setFrom(e.currentTarget.value)}
              />
              <TextInput
                label="Until"
                type="time"
                value={until}
                onChange={(e) => setUntil(e.currentTarget.value)}
              />
            </Group>
          )}

          <div>
            <Text size="sm" fw={500} mb={4}>
              What are you running?
            </Text>
            <Text size="xs" c="dimmed" mb="xs">
              Optional — attending to watch is fine, and capture does not depend on it. Entering a
              car is what lets a dashboard know which one is yours, and who is in it.
            </Text>

            {cars && cars.length === 0 ? (
              <Alert color="gray" variant="light">
                No cars set up yet.{' '}
                <Anchor component={Link} to={`/o/${orgId}/team`}>
                  Add one under Team
                </Anchor>{' '}
                and come back.
              </Alert>
            ) : (
              <Stack gap="xs">
                {entries.map((entry, i) => {
                  const car = cars?.find((c) => c.id === entry.carId)
                  return (
                    <Paper withBorder p="sm" key={entry.carId}>
                      <Group align="flex-end" wrap="nowrap">
                        <NumberInput
                          label="Number"
                          w={110}
                          value={entry.number}
                          hideControls
                          // The number can differ from the car's usual one for a
                          // single meeting, which is why it is stored on the
                          // entry rather than read from the car.
                          onChange={(v) =>
                            setEntries((es) =>
                              es.map((e, n) => (n === i ? { ...e, number: String(v) } : e)),
                            )
                          }
                        />
                        {/* What the dashboard filters a weekend's sessions on.
                            Chosen from the catalogue rather than typed: the
                            catalogue already records what each vendor's stream
                            calls a series, which is the hard half of matching,
                            and a stream that renames itself is then one alias
                            added centrally rather than every team correcting
                            their own spelling. */}
                        <Select
                          label="Series"
                          w={200}
                          placeholder={series?.length ? 'Which championship' : 'None set up yet'}
                          data={(series ?? []).map((s) => ({ value: s.id, label: s.name }))}
                          value={entry.seriesId ?? null}
                          onChange={(v) =>
                            setEntries((es) =>
                              es.map((x, n) => (n === i ? { ...x, seriesId: v ?? undefined } : x)),
                            )
                          }
                          disabled={!series?.length}
                          searchable
                          clearable
                        />
                        <MultiSelect
                          style={{ flex: 1 }}
                          label={`Drivers for ${car?.label || car?.number || 'this car'}`}
                          placeholder="Who is sharing it"
                          data={(drivers ?? []).map((d) => ({ value: d.id, label: d.name }))}
                          value={entry.driverIds}
                          onChange={(v) =>
                            setEntries((es) =>
                              es.map((e, n) => (n === i ? { ...e, driverIds: v } : e)),
                            )
                          }
                        />
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          mb={6}
                          onClick={() => setEntries((es) => es.filter((_, n) => n !== i))}
                        >
                          Remove
                        </Button>
                      </Group>
                    </Paper>
                  )
                })}

                <Group gap="xs">
                  {(cars ?? [])
                    .filter((c) => !entries.some((e) => e.carId === c.id))
                    .map((c) => (
                      <Button
                        key={c.id}
                        variant="light"
                        size="compact-sm"
                        onClick={() =>
                          setEntries((es) => [
                            ...es,
                            { carId: c.id, number: c.number, driverIds: [] },
                          ])
                        }
                      >
                        + {c.number}
                        {c.label ? ` ${c.label}` : ''}
                      </Button>
                    ))}
                </Group>
              </Stack>
            )}
          </div>

          {event && captureBlocker(event) && (
            <Alert color="yellow">
              {captureBlocker(event)}, so nothing will be recorded for it. Attaching is still worth
              doing — it is what asks for capture, and it will start the moment this vendor is
              supported.
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              {existing ? 'Save changes' : 'Attend this event'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

function formatRange(event: CatalogueEvent): string {
  const start = event.startsAt?.toDate?.()
  const end = event.endsAt?.toDate?.()
  if (!start) return ''
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: event.timezone || 'UTC',
  }
  const s = start.toLocaleDateString('en-GB', opts)
  if (!end) return s
  const e = end.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })
  return s === e.replace(/ \d{4}$/, '') ? e : `${s} – ${e}`
}
